import { AggregationPeriod, Button, Devices as DevicesApi, Facilities, Facility, FacilityEvent, FacilityEventType, getFacilityEventTypeDisplayName, OrganizationEvents, Reports, Target, useAuth, useDialog } from '@app/shared';
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useFetch } from "../../../hooks";
import { DataInfoCard, IconLink, Icons, Link, Select, SortableTable } from "../../shared";
import { DateOptions, IConfiguredTrafficReport, TrafficReportProps } from "../IConfiguredTrafficReport";
import { OrgReportContext, ReportContext } from "../reportHelpers";
import { ReportLoadingErrorWrapper } from "../ReportLoadingErrorWrapper";
import { DeviceMapShape, OmniGoogleMap, TargetMapShape } from '../../shared/maps';
import { toastWarning, useFacilityCenter } from '../../../helpers';
import { formatInTimeZone } from 'date-fns-tz';
import { TargetDetailsDialog, getEventDialogTitle } from '../../pages/facility/TargetDetailsDialog';
import { getEventTriggerName, loadEventTarget } from '@app/shared/src/components/LiveView';

export const ScreenlineReportViewLoader: React.FC<TrafficReportProps> = (props: TrafficReportProps | undefined) => {
    const { currentOrganizationId } = useAuth();
    const [devices, isDeviceLoading] = useFetch(() => DevicesApi.getByOrganizationId(currentOrganizationId), [currentOrganizationId]);

    const allReady = props && !isDeviceLoading && (props.size?.height ?? 0) > 10;
    return allReady ? (<ScreenlineReportView {...{ ...props, devices: devices }} />) : null;
}

const ScreenlineReportView: React.FC<TrafficReportProps> = (props) => {
    const { reportConfig, searchParams } = useContext(ReportContext);
    const { facilities: contextFacilities } = useContext(OrgReportContext);
    const [reportStep, setReportStep] = useState(0);
    const [reportType] = useState<FacilityEventType>(FacilityEventType.ScreenLineChange);
    const [direction, setDirection] = useState('');
    const [screenLineId, setScreenLineId] = useState('');
    const { currentOrganizationId } = useAuth();
    const contextFacilityId = reportConfig.context.facility?.id ?? reportConfig.context.device?.facilityId;
    const contextOrganizationId = reportConfig.context.facility?.organizationId ?? reportConfig.context.device?.organizationId ?? currentOrganizationId;
    const [orgFacilities, , facilitiesLoadHelpers] = useFetch(async () => (contextFacilities?.length ?? 0) > 0 ? [] : await Facilities.getByOrganizationId(contextOrganizationId), [contextOrganizationId]);
    const [targets, , targetsHelpers] = useFetch(() => Reports.getTotalVolume(reportConfig.grouping, AggregationPeriod.Day, searchParams), [searchParams]);
    const [centerCoords, centerIsLoading] = useFacilityCenter(reportConfig.context.facility ?? reportConfig.context.device?.facilityId);
    const selectedEventValue = useMemo(() => {
        if (direction.length < 1) { return undefined; }
        return direction === 'in' ? 1 : -1;
    }, [direction])
    const facilities = useMemo(() => contextFacilities && contextFacilities.length > 0
        ? contextFacilities
        : orgFacilities || [],
        [contextFacilities, orgFacilities]);
    const [events, , eventLoadHelpers] = useFetch(async () => await OrganizationEvents.getByOrganization(contextOrganizationId, 1000, undefined, "desc", reportType, (reportConfig.context.device ? reportConfig.context.device.id : undefined), contextFacilityId, screenLineId || undefined, selectedEventValue, reportConfig.inclusiveStart, reportConfig.exclusiveEnd, undefined, true), [reportType, contextOrganizationId, reportConfig.inclusiveStart, reportConfig.exclusiveEnd, selectedEventValue, screenLineId, contextFacilityId]);
    const [crossingCount, , crossingCountHelpers] = useFetch(async () => await OrganizationEvents.getCountByOrganization(contextOrganizationId, reportType, (reportConfig.context.device ? reportConfig.context.device.id : undefined), contextFacilityId, screenLineId || undefined, selectedEventValue, reportConfig.inclusiveStart, reportConfig.exclusiveEnd), [reportType, contextOrganizationId, reportConfig.inclusiveStart, reportConfig.exclusiveEnd, selectedEventValue, screenLineId, contextFacilityId]);
    const details = useDialog<{ target: Target | undefined; event: FacilityEvent }>();

    const targetCount = useMemo(() =>
        targets?.reduce((acc, val) => {
            return acc + val.count;
        }, 0)
        , [targets]);
    const isLoading = centerIsLoading || eventLoadHelpers.isLoading || crossingCountHelpers.isLoading || targetsHelpers.isLoading || facilitiesLoadHelpers.isLoading;
    const deviceColorMap = new Map<string, string>();
    facilities?.forEach(facility => {
        facility.devices?.forEach(device => {
            deviceColorMap.set(device.id!, device.color!);
        });
    });
    const deviceIdToFacilityMap = new Map<string, Facility>();
    facilities?.forEach(facility => {
        facility.devices?.forEach(device => {
            deviceIdToFacilityMap.set(device.id!, facility);
        });
    });
    const resolveFacility = (deviceId?: string, facilityId?: string): Facility | undefined =>
        (deviceId ? deviceIdToFacilityMap.get(deviceId) : undefined)
        ?? (facilityId ? facilities.find(f => f.id === facilityId) : undefined)
        ?? reportConfig.context.facility;
    useEffect(() => {
        if (events?.hasMore) {
            toastWarning('Large result', 'The map and table below show the first 1,000 crossings; the ScreenLine Crossings count is the full total.');
        }
    }, [events]);

    const devices = reportConfig.context.facility?.devices ?? (reportConfig.context.device ? [reportConfig.context.device] : undefined) ?? facilities?.flatMap(x => x.devices ?? []) ?? [];
    // const eventTypeFilterOptions = Object.values(FacilityEventType)
    //     .filter((value) => typeof value === "number")
    //     .map((value) => ({
    //         value: value.toString(),
    //         label: getFacilityEventTypeDisplayName(value as FacilityEventType) ?? "",
    //     }));

    // The target is supplementary, not a precondition — see loadEventTarget.
    const handleViewTargetDetails = async (event: FacilityEvent) =>
        details.show(getEventDialogTitle(event), "Okay", { target: await loadEventTarget(event), event }, () => { });

    const getTriggerName = (event: FacilityEvent) => {
        return getEventTriggerName(event, facilities?.find((f) => f.id === event.facilityId)) ?? "";
    };

    const screenLineOptions = useMemo(() => {
        const relevant = contextFacilityId
            ? (facilities?.filter(f => f.id === contextFacilityId) ?? [])
            : (facilities ?? []);
        const multiFacility = relevant.length > 1;
        const seen = new Set<string>();
        const options = [{ label: 'All Screen Lines', value: '' }];
        relevant.forEach(facility => {
            facility.screenLines?.forEach(sl => {
                if (sl.id && !seen.has(sl.id)) {
                    seen.add(sl.id);
                    const name = sl.name ?? 'Unnamed Screen Line';
                    options.push({ label: multiFacility ? `${facility.name}: ${name}` : name, value: sl.id });
                }
            });
        });
        return options;
    }, [facilities, contextFacilityId]);

    const LaneSelector = (
        <div>
            {/* <Select
                label="Select Event Type"
                value={reportType}
                onChange={(val) => setReportType(parseInt(val.target.value) as FacilityEventType)}
                options={eventTypeFilterOptions}
            ></Select> */}
            {reportType === FacilityEventType.ScreenLineChange && screenLineOptions.length > 1 &&
                <Select
                    label="Screen Line"
                    value={screenLineId}
                    onChange={e => setScreenLineId(e.target.value)}
                    options={screenLineOptions}
                ></Select>
            }
            {reportType === FacilityEventType.ScreenLineChange &&
                <Select
                    label="Direction"
                    value={direction}
                    onChange={e => setDirection(e.target.value)}
                    options={[{ label: 'Any Direction', value: '' }, { label: 'Inbound', value: 'in' }, { label: 'Outbound', value: 'out' }]}
                ></Select>
            }
            {<Button
                disabled={!!isLoading}
                className="mt-2"
                onClick={async () => {
                    setReportStep(1);
                }}>{(!!isLoading) ? 'Loading...' : 'Create Report'}</Button>
            }
        </div>
    );

    if (reportStep < 1) {
        return (LaneSelector);
    }

    return <ReportLoadingErrorWrapper error={undefined} hasNoData={false} isLoading={isLoading} size={props.size} >
        <div className="print:hidden w-full flex flex-row">
            <div className="flex-grow">
                <button onClick={() => setReportStep(0)}>
                    <IconLink Icon={Icons.ArrowBack}>Go Back</IconLink>
                </button>
            </div>

        </div>
        {!isLoading && <OmniGoogleMap center={centerCoords} fitToPolygons={true}>

            {devices.map(d => {
                const deviceFacility = resolveFacility(d.id, d.facilityId);
                return deviceFacility ? <DeviceMapShape key={d.id} facility={deviceFacility} shape={d} /> : null;
            })}
            {events!.data?.map(e => {
                if (!e.deviceId) return null;
                const eventFacility = resolveFacility(e.deviceId, e.facilityId);
                return eventFacility ? <TargetMapShape key={e.id} facility={eventFacility} target={e.target} color={deviceColorMap.get(e.deviceId)} /> : null;
            })}

        </OmniGoogleMap>}

        <div className="flex">
            <div className="flex gap-4 m-6 ms-0">
                <DataInfoCard title='Targets' Icon={Icons.Target}><h1>{targetCount}</h1></DataInfoCard>
                <DataInfoCard title='ScreenLine Crossings' Icon={direction.length < 1 ? Icons.ArrowsLeftRight : direction === 'in' ? Icons.Entry : Icons.Exit}><h1>{direction.length < 1 ? '' : direction === 'in' ? 'Inbound' : 'Outbound'} {(crossingCount ?? 0).toLocaleString()}</h1></DataInfoCard>
            </div>
        </div>

        <div className='max-h-vh mt-5' >
            <SortableTable
                columns={[
                    {
                        header: "Date",
                        dataKey: "dateCreated",
                        width: 100,
                        renderFunc: (fe) => (fe?.dateCreated ? formatInTimeZone(fe?.dateCreated, facilities?.find(x => x.id === fe.id)?.localTimezoneId ?? 'UTC', "MM-dd p") : ""),
                    },
                    {
                        header: "Location",
                        dataKey: "facilityId",
                        width: 150,
                        renderFunc: (fe) => facilities?.find(x => x.id === fe?.facilityId)?.name,
                    },
                    {
                        header: "Event Type",
                        dataKey: "eventType",
                        width: 100,
                        renderFunc: (fe) => getFacilityEventTypeDisplayName(fe!.eventType),
                    },
                    {
                        header: "Device",
                        width: 150,
                        dataKey: "deviceId",
                        renderFunc: (fe) => facilities?.find(x => x.id === fe?.facilityId)?.devices?.find(x => x.id === fe?.deviceId)?.name ?? "",
                    },
                    {
                        header: "Trigger",
                        width: 100,
                        dataKey: "screenLineId",
                        renderFunc: (fe) => getTriggerName(fe!),
                    },
                    {
                        header: "Value",
                        width: 100,
                        dataKey: "eventValue",
                        renderFunc: (fe) => {
                            switch (fe?.eventType) {
                                case FacilityEventType.ScreenLineChange:
                                    return fe?.eventValue >= 1 ? "Entry" : "Exit";
                                case FacilityEventType.DetectionZoneChange:
                                    return fe?.eventValue >= 1 ? "Entry" : "Exit";
                                case FacilityEventType.DetectionZonePresence:
                                    return (fe?.eventValue ? fe?.eventValue : "No") + " Target" + (fe?.eventValue !== 1 ? "s" : "");
                                case FacilityEventType.Speeding:
                                    return ((fe?.eventValue ?? 0) * 2.23694).toFixed(1) + " mph";
                                case FacilityEventType.DetectionZoneDwell:
                                    return ">" + fe?.eventValue + " second" + (fe?.eventValue !== 1 ? "s" : "");
                                default:
                                    return fe?.eventValue;
                            }
                        },
                    },
                    {
                        header: "",
                        width: 75,
                        renderFunc: (fe) => (fe ? <Link onClick={() => handleViewTargetDetails(fe)}>View Details</Link> : ""),
                    },
                ]}
                data={events?.data?.map((event) => ({
                    ...event,
                    id: event.id || event.dateCreated?.toString() || "",
                }))}
                isLoading={isLoading}
                emptyValue={"No Events were found"}
            />
            {details.renderDialog(
                (val) => {
                    // Event's facilityId first — it's always present, whereas keying off the target's
                    // device fails for any event whose target couldn't be loaded (see
                    // OrganizationEventsTable). resolveFacility supplies the device and report-context
                    // fallbacks after that.
                    const facility = resolveFacility(val?.event?.deviceId ?? val?.target?.deviceId, val?.event?.facilityId);
                    return facility ? <TargetDetailsDialog facility={facility} target={val?.target} event={val?.event} showVideoSection /> : <div>Location not found</div>;
                },
                { closeOnOutsideClick: true, showCancelButton: false, className: "w-full max-w-3xl my-4 max-h-[calc(100vh-2rem)] overflow-y-auto" }
            )}
        </div>
    </ReportLoadingErrorWrapper >
};

export const ScreenlineMap = {
    name: "Screenline Crossing Map",
    description: "Shows map of crossing paths and table of crossing events",
    component: ScreenlineReportViewLoader,
    key: 'scr',
    defaultChartType: 'other',
    defaultRange: DateOptions.ThisWeek,
    config: {
        requiresMonth: false,
        allowsCustomRange: true,
        requiresYear: false,
        disallowChartTypeToggling: true,
        allowTargetTypeGrouping: true,
        hideDeviceSelection: true
    }
} as IConfiguredTrafficReport;